/**
 * Canvas renderer.
 *
 * Draws a CadDocument the way the original does: geometry is stroked at its
 * real width, pads are drawn per their type, and layer colour comes from the
 * layer's own `screen` value rather than an invented palette.
 */

import {
  type ArcEntity, type CadDocument, type ComponentEntity, type Entity,
  type FillEntity, type Layer, type LineEntity, type PadEntity,
  type ImageRef, type SpiralEntity, type TextAnchor, type TextEntity,
  LayerType, padIsSchematic,
} from '@sircad/core/model.ts';
import type { RatLine } from '@sircad/core/net/netlist.ts';
import { Viewport } from './viewport.ts';
import { drawText, layoutText, substituteParams } from './text.ts';
import { padOutline, padShape, padThrough, padIsRectangular } from '@sircad/core/geom/pad.ts';
import type { Spoke } from '@sircad/core/net/thermal.ts';
import { worldBounds, overlaps, type Box } from './bounds.ts';

/** A part's claimed area, and what is wrong with where it is. */
export interface DensityFault {
  pts: { x: number; y: number }[];
  kind: 'overlap' | 'straddle' | 'parked';
}

/** The congestion grid, flattened to what a frame needs to paint it. */
export interface DensityOverlay {
  x0: number;
  y0: number;
  cell: number;
  nx: number;
  ny: number;
  /** demand / capacity per cell. Infinity where nothing fits through. */
  pressure: Float32Array;
  faults: DensityFault[];
}

export interface RenderOptions {
  /** Entity ids drawn in the highlight colour. */
  selected?: ReadonlySet<number>;
  /** Entity ids drawn in the attention colour, e.g. DRC violations. */
  flagged?: ReadonlySet<number>;
  /** Entity ids on a highlighted net (Nets ▸ Highlight Net), drawn in a
   *  distinct colour so one net stands out across the whole board. */
  highlighted?: ReadonlySet<number>;
  /** Ratsnest to overlay. */
  ratsnest?: RatLine[];
  /**
   * Routing density and placement faults, laid over the board.
   *
   * Built by the caller from `route/congestion.ts` rather than computed here —
   * the renderer draws, and a frame must not depend on a sweep of the netlist.
   */
  density?: DensityOverlay;
  /**
   * Photographs to lay under the drawing, with the pixels to draw.
   *
   * The document holds only where a picture goes; the pixels are loaded per
   * session and handed in here, which is what keeps a phone photograph out of
   * a board file. A reference with nothing loaded simply does not draw.
   */
  images?: { ref: ImageRef; pixels: CanvasImageSource }[];
  /**
   * How strongly the photographs show, 0 to 1.
   *
   * Tracing wants the picture faint: strong enough to see a track on, faint
   * enough that the track you draw over it is the thing your eye follows.
   */
  imageOpacity?: number;
  /** Draw the grid dots. */
  showGrid?: boolean;
  gridSpacing?: number;
  /** Dim everything not on the active layer, as the original's grey mode does. */
  activeLayer?: number;
  dimInactive?: boolean;
  /** Transient geometry drawn above the document, e.g. a tool's rubber band. */
  preview?: Entity[];
  /**
   * Preview ids to draw in the highlight colour instead of their layer's.
   *
   * A rubber band is easier to read in the colour of the thing it will become —
   * a signal wire should look like a signal wire while you are drawing it, not
   * like a white line. What is *not* geometry, though, has no layer colour to
   * borrow: the rectangle drawn while marking a block is an instrument, not
   * part of the drawing, so it stays highlighted.
   */
  previewAccent?: ReadonlySet<number>;
  /**
   * Dash offset for the accented preview, in screen pixels, or 0 for a solid
   * line.
   *
   * The original's block perimeter is animated — a marching dashed outline, so
   * that the region you have marked reads as an instrument laid over the
   * drawing rather than as four lines somebody drew. It is also how you find
   * the edges of a block on a busy sheet, where a thin solid rectangle
   * disappears into the wiring.
   */
  previewDash?: number;
  /**
   * Ids to draw hollow, whatever the document's draw modes say.
   *
   * What is being carried is not yet where it belongs, and an outline says so
   * without hiding the board underneath it — you can see the pads you are
   * lining it up against straight through the thing in your hand.
   */
  outlined?: ReadonlySet<number>;
  /**
   * Full-canvas crosshair at this document-space point, drawn while placing a
   * line or component — the original's large X/Y cursor. `diagonal` draws the
   * 45° pair the original shows in 45-only ortho mode.
   */
  crosshair?: { x: number; y: number; diagonal?: boolean };
  /**
   * Draw the cross-hair three pixels wide rather than one.
   *
   * The original's Thick Cross-hair Cursor, on out of the box and switchable
   * on Setup > Options. One pixel at half opacity disappears into copper; a
   * moving three-pixel cross does not.
   */
  thickCrosshair?: boolean;
  /**
   * Reuse the cached flattened entity tree instead of re-walking the document.
   * Only safe when the *geometry* is unchanged since the last draw — the caller
   * passes it for pure viewport moves (pan, wheel-zoom), never after an edit.
   */
  reuseFlatten?: boolean;
  /**
   * Draw for paper: each layer in its *print* colour on white, rather than its
   * screen colour on the dark desktop. CIRCAD keeps the two separately per
   * layer, because what reads well on a monitor is not what reads well printed.
   */
  printColors?: boolean;
  /**
   * The view is being moved right now, so favour a quick frame.
   *
   * Thresholds tighten and the least useful detail is left out while a pan or
   * a zoom is in flight; the caller draws again without this once the view
   * settles, and the full picture arrives a moment later. On a board of forty
   * thousand objects that is the difference between a view that follows the
   * mouse and one that lurches after it.
   */
  interacting?: boolean;
  /**
   * Write each pad's pin number across it.
   *
   * Off by default: on a dense board the numbers cover the copper they label.
   * It is something you turn on to check a footprint against its part, or to
   * find pin 1, and turn off again to route.
   */
  showPins?: boolean;
}

/** What a frame actually did, for the status line and for tuning. */
export interface RenderStats {
  /** Leaves handed to the canvas. */
  drawn: number;
  /** Skipped for being outside the view. */
  culled: number;
  /** Skipped for being smaller than a pixel or so. */
  tiny: number;
  /** Text skipped for being too small to read. */
  illegible: number;
  /** Fill outline vertices drawn. */
  vertices: number;
  /** Fill vertices dropped for landing on a pixel already occupied. */
  verticesSkipped: number;
  /** Pour clearances actually drawn. */
  knockouts: number;
  /** Pour clearances skipped for being off screen, off the pour, or too small. */
  knockoutsSkipped: number;
  /** Milliseconds spent in the last frame. */
  ms: number;
}

/** Accumulated transform for entities inside a component. */
interface Xform {
  /** Translation. */
  tx: number;
  ty: number;
  cos: number;
  sin: number;
  mirror: number;
  /** Owning component, for &n parameter substitution. */
  params: Record<number, string> | null;
  /** Net attached to the owning part, for the &@ marker (port labels). */
  net: string | null;
}

const IDENTITY: Xform = { tx: 0, ty: 0, cos: 1, sin: 0, mirror: 1, params: null, net: null };

/** An anchor reflected left to right: BL becomes BR, TR becomes TL, centres stay. */
function flipAnchorH(a: TextAnchor | undefined): TextAnchor {
  const s = a ?? 'BL';
  const h = s.charAt(1);
  const flipped = h === 'L' ? 'R' : h === 'R' ? 'L' : h;
  return (s.charAt(0) + flipped) as TextAnchor;
}

/** Shared empty id set, so a frame with no accents allocates nothing. */
const EMPTY_IDS: ReadonlySet<number> = new Set<number>();

/** A pad in world space, used to clear a flood pour around off-net copper. */
interface WorldPad {
  x: number; y: number; rx: number; ry: number; rot: number;
  rect: boolean; through: boolean; layer: number; signal?: string;
  /** The pad itself, placed, for geometry shared with the plotter. */
  world: PadEntity;
}
/** A line in world space, likewise. */
interface WorldLine {
  x1: number; y1: number; x2: number; y2: number;
  width: number; layer: number; signal?: string;
}

/** A leaf, where it sits, and the box it occupies. */
interface Placed {
  e: Entity;
  m: Xform;
  /** World box, or null for something with no extent to cull against. */
  b: Box | null;
}

interface Flatten {
  doc: CadDocument;
  byLayer: Map<number, Placed[]>;
  orphans: Placed[];
  frame: { pads: WorldPad[]; lines: WorldLine[] } | null;
}

function compose(parent: Xform, c: ComponentEntity): Xform {
  const rad = ((c.rotation ?? 0) * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const mirror = c.mirrored ? -1 : 1;
  // The component's own origin, expressed in the parent's frame.
  const px = c.x * parent.mirror;
  return {
    tx: parent.tx + px * parent.cos - c.y * parent.sin,
    ty: parent.ty + px * parent.sin + c.y * parent.cos,
    cos: parent.cos * cos - parent.sin * sin,
    sin: parent.sin * cos + parent.cos * sin,
    mirror: parent.mirror * mirror,
    params: c.params,
    // A port labels itself with the net on its pin (`&@`); inherit the parent's
    // net for nested text that has no pin of its own.
    net: portNet(c) ?? parent.net,
  };
}

/** The net a part connects to: the signal on its first signalled pad. */
function portNet(c: ComponentEntity): string | null {
  for (const child of c.children) {
    if (child.kind === 'pad' && child.signal) return child.signal;
  }
  return null;
}

/** Whether any entity is a flood pour, so the per-frame clearance geometry
 *  (pads/lines in world space) is worth collecting. Imported plane-as-lines
 *  designs have none, and the collection is pure waste for them. */
function hasFloodFill(list: Entity[]): boolean {
  for (const e of list) {
    if (e.kind === 'fill' && (e as FillEntity).flood) return true;
    if (e.kind === 'component' && hasFloodFill(e.children)) return true;
  }
  return false;
}

function tx(x: number, y: number, m: Xform): [number, number] {
  const mx = x * m.mirror;
  return [m.tx + mx * m.cos - y * m.sin, m.ty + mx * m.sin + y * m.cos];
}

/**
 * Where an arc sits and how far it runs, once its component's placement is
 * folded in. Angles in degrees, centre in document units.
 *
 * Pulled out of the drawing so the one piece of this that is easy to get wrong
 * can be checked: a mirror here reflects *x* — see {@link tx} — which sends an
 * angle θ to 180−θ and reverses the sweep. It used to send it to −θ, the
 * reflection about the other axis, which leaves an arc exactly half a turn
 * round its own circle. On a capacitor that put the curved plate an inch away
 * from the straight one, looking for all the world like the arc had been
 * displaced rather than turned.
 */
export function arcGeometry(
  e: ArcEntity, m: Xform,
): { cx: number; cy: number; start: number; sweep: number; axis: number } {
  const [cx, cy] = tx(e.cx, e.cy, m);
  const baseRot = (Math.atan2(m.sin, m.cos) * 180) / Math.PI;
  // An arc's own Rotation offsets its zero point: for a circle that turns the
  // start, while an ellipse turns its axis instead.
  const isEllipse = !!e.eccentricity && e.eccentricity !== 1;
  const spin = isEllipse ? 0 : (e.rotation ?? 0);
  const local = e.angle + spin;

  // A zero sweep is a whole circle, which is how the model records one and how
  // the writers already treat it. Taken literally it is an arc of no extent,
  // and canvas draws nothing at all for it: a KiCad `fp_circle` comes in this
  // way, so component body outlines simply were not there — an electrolytic
  // capacitor rendered as its bare polarity stripe.
  const span = e.sweep === 0 ? 360 : e.sweep;

  return {
    cx,
    cy,
    start: (m.mirror < 0 ? 180 - local : local) + baseRot,
    sweep: span * m.mirror,
    // An ellipse's axis is half-turn periodic, so reflecting it is a negation.
    axis: (e.rotation ?? 0) * m.mirror + baseRot,
  };
}

/** The pad master, where every through pad is drawn. */
const PAD_MASTER = 0;

/** A through-hole pad, which reaches every layer whatever it is tagged with. */
function padIsThrough2(e: Entity): boolean {
  return e.kind === 'pad' && padThrough(e);
}

const hex = (rgb: number): string => `#${(rgb >>> 0).toString(16).padStart(6, '0').slice(-6)}`;

/**
 * Draw modes, as CIRCAD packs them into `Display:: DrawModes:` (`fDraw` in
 * OGxyz03.cpp): byte 0 is lines (and arcs, spirals, fills), byte 1 pads, byte 2
 * text. The low two bits pick how solid the object is drawn:
 *
 *   0 centre line — a hairline through the middle, the fastest
 *   1 outline     — just the edges, so overlapping copper stays readable
 *   2 sparse fill — outline plus interior lines; we render as outline
 *   3 dense fill  — solid at true width (the default, and what looks "real")
 */
export const DrawMode = { CentreLine: 0, Outline: 1, SparseFill: 2, DenseFill: 3 } as const;

/**
 * Pad draw modes, which are *not* the line ones.
 *
 * The original's Setup Parameters page offers lines "Default / Outline only /
 * Sparse fill / Dense fill" but pads "Outline only / Outline with hole / Dense
 * fill / Dense fill/no hole" (OG3.rc, groups 0x1100 and 0x1120). The byte is
 * the same shape, the meanings are not: a pad byte of 2 is *filled*, where a
 * line byte of 2 is a sparse fill. Reading pads with the line table drew every
 * pad on a board whose `DrawModes` ends 06 as a one-pixel hollow ring — which
 * makes a via all but invisible and looks like it was never decoded.
 */
export const PadDrawMode = {
  Outline: 0,
  OutlineWithHole: 1,
  Filled: 2,
  FilledNoHole: 3,
} as const;

function lineDrawMode(doc: CadDocument): number {
  return (doc.display?.drawModes ?? 3) & 3;
}
function padDrawMode(doc: CadDocument): number {
  return ((doc.display?.drawModes ?? 0x300) >> 8) & 3;
}
function textDrawMode(doc: CadDocument): number {
  return ((doc.display?.drawModes ?? 0x30000) >> 16) & 3;
}

export class Renderer {
  /** Per-frame world geometry, used to clear flood pours. Set during render. */
  private frame: { pads: WorldPad[]; lines: WorldLine[] } | null = null;

  /**
   * What the last frame did. Read by the shell for the status line, and the
   * only honest way to tell whether a change to the thresholds helped.
   */
  /** Whether this frame is being drawn while the view moves. */
  private interacting = false;

  readonly stats: RenderStats = {
    drawn: 0, culled: 0, tiny: 0, illegible: 0,
    vertices: 0, verticesSkipped: 0, knockouts: 0, knockoutsSkipped: 0, ms: 0,
  };

  /**
   * Active stroke batch, keyed by colour+width. When set, {@link drawLine} and
   * {@link drawArc} append to a shared {@link Path2D} instead of stroking one at
   * a time; {@link flushBatch} strokes each once. A flooded plane is thousands of
   * same-style segments, so this turns thousands of `stroke()` calls into a
   * handful — the difference between a laggy and a smooth board with all layers on.
   */
  private strokeBatch: Map<string, { color: string; width: number; path: Path2D }> | null = null;

  /**
   * The flattened entity tree (leaves tagged with their world transform),
   * cached between frames. Walking it is viewport-independent, so a pan or zoom
   * can reuse it — {@link RenderOptions.reuseFlatten} — turning the per-frame
   * document walk into a no-op. Any edit takes the normal path, which rebuilds.
   */
  private flatCache: Flatten | null = null;

  /** Draw modes for this frame, decoded from the document once per render. */
  private modes = { line: 3, pad: 3, text: 3 };

  /** Background for this frame — the file's desktop colour, or white when
   *  plotting. Knock-outs punch holes in copper with it, so it must match. */
  private bg = 0x101418;

  /** Label each pad with its pin number this frame. */
  private showPins = false;

  /**
   * Pin numbers held back until every layer has been drawn.
   *
   * Written as each pad is drawn they were promptly buried: a pour, or simply
   * the next copper layer, paints over whatever the layer below left behind.
   * A label is not part of the board, so it belongs above all of it — the same
   * reasoning that puts the ratsnest in its own pass at the end.
   */
  private pinLabels: {
    pin: string; sx: number; sy: number; size: number; room: number; color: string;
  }[] = [];

  // Written out rather than declared as constructor parameter properties: that
  // is the one piece of TypeScript here that is not simply erasable, and it
  // stops this module being loaded by the checks in tools/, which want to ask
  // it how an arc is placed without standing up a browser. Same reasoning as
  // MenuSystem's constructor.
  private readonly canvas: HTMLCanvasElement;
  readonly viewport: Viewport;

  constructor(canvas: HTMLCanvasElement, viewport: Viewport) {
    this.canvas = canvas;
    this.viewport = viewport;
  }

  /** Resize the backing store to the element's CSS size and device ratio. */
  resize(): void {
    const rect = this.canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    this.viewport.width = rect.width;
    this.viewport.height = rect.height;
    this.viewport.dpr = dpr;
    const w = Math.max(1, Math.round(rect.width * dpr));
    const h = Math.max(1, Math.round(rect.height * dpr));
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
    }
  }

  render(doc: CadDocument, opts: RenderOptions = {}): void {
    const ctx = this.canvas.getContext('2d');
    if (!ctx) return;
    const vp = this.viewport;
    const began = performance.now();
    this.stats.drawn = 0;
    this.stats.culled = 0;
    this.stats.tiny = 0;
    this.stats.illegible = 0;
    this.interacting = opts.interacting === true;
    this.stats.vertices = 0;
    this.stats.verticesSkipped = 0;
    this.stats.knockouts = 0;
    this.stats.knockoutsSkipped = 0;

    ctx.setTransform(vp.dpr, 0, 0, vp.dpr, 0, 0);
    ctx.clearRect(0, 0, vp.width, vp.height);

    // Paper is white; the desktop is whatever the file says. Held for the frame
    // so the knock-outs that punch holes in copper use the same colour.
    this.bg = opts.printColors ? 0xffffff : (doc.display?.backgroundColor ?? 0x101418);
    ctx.fillStyle = hex(this.bg);
    ctx.fillRect(0, 0, vp.width, vp.height);

    this.modes = {
      line: lineDrawMode(doc), pad: padDrawMode(doc), text: textDrawMode(doc),
    };
    this.showPins = opts.showPins === true;
    this.pinLabels.length = 0;

    this.drawImages(ctx, opts);

    if (opts.showGrid !== false) this.drawGrid(ctx, doc, opts);

    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    this.drawByLayer(ctx, doc, opts);

    // Then whatever is in hand, hollow and on top. Drawing it here rather than
    // in its layer's turn keeps it above the board it is being placed onto,
    // which is the one thing you need to see while lining it up.
    if (opts.outlined?.size) {
      const saved = this.modes;
      this.modes = {
        line: DrawMode.Outline, pad: PadDrawMode.Outline, text: saved.text,
      };
      const held: RenderOptions = {
        activeLayer: opts.activeLayer,
        selected: opts.selected,
        highlighted: opts.highlighted,
      };
      for (const e of doc.entities) {
        if (opts.outlined.has(e.id)) this.drawEntity(ctx, doc, e, IDENTITY, held);
      }
      this.modes = saved;
    }

    // Above every layer, so a pour or the next layer's copper cannot bury them.
    this.drawPinNumbers(ctx);

    // Under the ratsnest: the flylines are what the density is *of*, and
    // reading them against it is the whole point.
    if (opts.density) this.drawDensity(ctx, opts.density);

    if (opts.ratsnest?.length) this.drawRatsnest(ctx, doc, opts.ratsnest);

    // Tool preview sits above everything, in the colour of the layer each piece
    // will land on — a wire being dragged out on the signal layer should look
    // like that wire, not like a white line that happens to be where it goes.
    // Only what the caller marks as an instrument is highlighted instead.
    if (opts.preview?.length) {
      const previewOpts: RenderOptions = {
        // Treating an entity as selected routes it through the highlight colour
        // without duplicating the drawing code, so the accent set is the
        // selection here and everything else takes its own layer's colour.
        selected: opts.previewAccent ?? EMPTY_IDS,
        activeLayer: opts.activeLayer,
      };
      // The accented pieces — the block outline, and nothing else — march.
      // Split into two passes rather than dashing everything: a wire being
      // dragged out must still look like a wire.
      const accent = opts.previewAccent;
      const dash = opts.previewDash ?? 0;
      for (const e of opts.preview) {
        if (dash > 0 && accent?.has(e.id)) continue;
        this.drawEntity(ctx, doc, e, IDENTITY, previewOpts);
      }
      if (dash > 0 && accent?.size) {
        ctx.save();
        ctx.setLineDash([6, 4]);
        ctx.lineDashOffset = -dash;
        for (const e of opts.preview) {
          if (accent.has(e.id)) this.drawEntity(ctx, doc, e, IDENTITY, previewOpts);
        }
        ctx.restore();
      }
    }

    if (opts.crosshair) {
      this.drawCrosshair(ctx, doc, opts.crosshair, opts.thickCrosshair !== false);
    }
    this.stats.ms = performance.now() - began;
  }

  /**
   * The original's large cursor: lines spanning the whole canvas through the
   * cursor point, so the placement position is readable against distant
   * geometry. Horizontal/vertical normally, a 45° pair when the tool is in
   * diagonal ortho mode (OGxyz04.cpp:1008).
   */
  private drawCrosshair(
    ctx: CanvasRenderingContext2D,
    doc: CadDocument,
    at: { x: number; y: number; diagonal?: boolean },
    thick: boolean,
  ): void {
    const vp = this.viewport;
    const sx = vp.toScreenX(at.x);
    const sy = vp.toScreenY(at.y);
    const w = vp.width;
    const h = vp.height;

    ctx.save();
    ctx.strokeStyle = hex(doc.display?.highlightColor ?? 0xffffff);
    ctx.globalAlpha = 0.55;
    // Three pixels, not one. The original's Thick Cross-hair Cursor is on out
    // of the box — its preference reads "anything but No" (OG1.cpp:1143), so an
    // absent key leaves it set — and it draws a line either side of the middle
    // one (OGxyz04.cpp:1048). It is the difference between a cursor you can
    // follow across a busy board and one that disappears into the copper, and
    // it is switchable on Setup > Options for anyone who wants the hairline.
    ctx.lineWidth = thick ? 3 : 1;
    ctx.setLineDash([]);
    ctx.beginPath();
    if (at.diagonal) {
      const r = Math.hypot(w, h);
      ctx.moveTo(sx - r, sy - r); ctx.lineTo(sx + r, sy + r);
      ctx.moveTo(sx - r, sy + r); ctx.lineTo(sx + r, sy - r);
    } else {
      ctx.moveTo(0, sy); ctx.lineTo(w, sy);
      ctx.moveTo(sx, 0); ctx.lineTo(sx, h);
    }
    ctx.stroke();
    ctx.restore();
  }

  // -------------------------------------------------------------------------

  private layerOf(doc: CadDocument, n: number): Layer | undefined {
    return doc.layers[n];
  }

  private colorFor(
    doc: CadDocument, e: Entity, opts: RenderOptions,
  ): string | null {
    // Held out of the ordinary pass and drawn hollow afterwards, above the
    // board, so what is being carried reads as not yet placed.
    if (opts.outlined?.has(e.id)) return null;
    // A through pad takes the pad master's colour, not that of the layer it is
    // tagged with. `Pad:: Layer:"GND"` marks a hole to be relieved into the
    // ground plane; the hole itself belongs to every layer, and the original
    // draws it in the pad master colour. It is drawn in the pad master's pass
    // too, so taking the colour from anywhere else left it a different colour
    // from the pad beside it on the same part.
    const layerIndex = padIsThrough2(e) ? PAD_MASTER : e.layer;
    const layer = this.layerOf(doc, layerIndex);

    // On paper there is no selection, no highlight and no DRC flash — a plot is
    // of the drawing, not of the editor's state — and each layer uses the print
    // colour the file gives it.
    if (opts.printColors) {
      if (layer && !layer.visible) return null;
      const p = ('color' in e && e.color !== undefined) ? e.color : layer?.print ?? 0x000000;
      // A layer printing white on white would vanish; black is the safe read.
      return hex(p === 0xffffff ? 0x000000 : p);
    }

    if (opts.selected?.has(e.id)) return hex(doc.display?.highlightColor ?? 0xffffff);
    // A highlighted net stands out in bright cyan — distinct from selection
    // (white) and DRC flags (yellow) so it reads as "this whole net".
    if (opts.highlighted?.has(e.id)) return hex(0x33ffff);
    if (opts.flagged?.has(e.id)) return hex(doc.display?.attentionColor ?? 0xffff00);

    if (layer && !layer.visible) return null;

    const rgb = ('color' in e && e.color !== undefined) ? e.color : layer?.screen ?? 0x888888;
    if (opts.dimInactive && opts.activeLayer !== undefined && layerIndex !== opts.activeLayer) {
      // Blend toward the background rather than using alpha, so overlapping
      // dimmed geometry does not accumulate into something brighter.
      return dim(rgb, doc.display?.backgroundColor ?? 0x101418, 0.55);
    }
    return hex(rgb);
  }

  /** Stroke width in CSS pixels, never thinner than a hairline. */
  private penWidth(w: number): number {
    return Math.max(1, w * this.viewport.scale);
  }

  /**
   * Stroke a path, or add it to the current batch. `build` receives a Path2D to
   * append to. When batching, paths that share a colour and width are merged and
   * stroked once by {@link flushBatch}; otherwise the path is stroked immediately.
   */
  private strokePath(
    ctx: CanvasRenderingContext2D, color: string, widthPx: number,
    build: (p: Path2D) => void,
  ): void {
    if (this.strokeBatch) {
      const key = `${color}|${widthPx.toFixed(2)}`;
      let entry = this.strokeBatch.get(key);
      if (!entry) {
        entry = { color, width: widthPx, path: new Path2D() };
        this.strokeBatch.set(key, entry);
      }
      build(entry.path);
      return;
    }
    const p = new Path2D();
    build(p);
    ctx.strokeStyle = color;
    ctx.lineWidth = widthPx;
    ctx.stroke(p);
  }

  /** Stroke every batched path (one call per colour+width) and clear the batch. */
  private flushBatch(ctx: CanvasRenderingContext2D): void {
    if (!this.strokeBatch) return;
    for (const { color, width, path } of this.strokeBatch.values()) {
      ctx.strokeStyle = color;
      ctx.lineWidth = width;
      ctx.stroke(path);
    }
    this.strokeBatch.clear();
  }

  /**
   * Draw the document in layer order.
   *
   * The original paints one layer at a time, so a lower layer sits under the
   * ones above it and the active layer is drawn last, on top (OG's ZL command,
   * "select a new active layer to be displayed on top"). We reproduce that: the
   * whole entity tree — including geometry nested inside components — is
   * flattened to leaves tagged with their layer, then drawn deepest layer
   * first (the highest stack index, e.g. copper and planes) up to the shallow
   * ones (silk, symbols, the pad master), with the active layer pulled to the
   * very top. Within one layer, fills are laid down before strokes so a poured
   * plane never buries the traces sharing its layer.
   */
  /** Walk the document to drawable leaves. Viewport-independent, so the result
   *  is cached and reused across pan/zoom frames (see {@link flatCache}). */
  private buildFlatten(doc: CadDocument): Flatten {
    const byLayer = new Map<number, Placed[]>();
    const orphans: Placed[] = [];
    const n = doc.layers.length;

    // The clearance geometry is only read when a flood pour is drawn; skip the
    // world-space collection entirely when there is none to clear.
    const needFrame = hasFloodFill(doc.entities);
    const pads: WorldPad[] = [];
    const lines: WorldLine[] = [];
    const collect = (e: Entity, m: Xform): void => {
      if (e.kind === 'component') {
        const inner = compose(m, e);
        for (const c of e.children) collect(c, inner);
        return;
      }
      if (needFrame && e.kind === 'pad' && !padIsSchematic(e.type)) {
        const [wx, wy] = tx(e.x, e.y, m);
        pads.push({
          x: wx, y: wy, rx: e.sizeX / 2, ry: e.sizeY / 2,
          rot: ((e.rotation ?? 0) * Math.PI) / 180 + Math.atan2(m.sin, m.cos) * m.mirror,
          rect: padIsRectangular(e), through: padThrough(e), layer: e.layer, signal: e.signal,
          // Kept so a thermal relief can be built by the one function that
          // builds them, rather than from these few numbers a second time.
          world: {
            ...e, x: wx, y: wy,
            rotation: (e.rotation ?? 0)
              + ((Math.atan2(m.sin, m.cos) * 180) / Math.PI) * m.mirror,
          },
        });
      } else if (needFrame && e.kind === 'line') {
        const [ax, ay] = tx(e.x1, e.y1, m);
        const [bx, by] = tx(e.x2, e.y2, m);
        lines.push({ x1: ax, y1: ay, x2: bx, y2: by, width: e.width, layer: e.layer, signal: e.signal });
      }
      // A through pad is drawn with the pad master, whatever layer it is
      // tagged with. `Pad:: Layer:"GND" Type:T1` marks a hole to be relieved
      // into the ground plane, not a hole that exists only on that plane — and
      // drawing it in the plane's colour, buried in the plane's draw order,
      // made it look like a different object or none at all. The original
      // draws every one of them in the pad master colour.
      const drawnOn = padIsThrough2(e) ? PAD_MASTER : e.layer;
      // The box is worked out once, here, and carried on the item. The flatten
      // survives a pan or a zoom, so this is paid once per edit rather than
      // once per frame — which is the whole reason culling is affordable.
      const b = worldBounds(e, m);
      if (drawnOn >= 0 && drawnOn < n) {
        (byLayer.get(drawnOn) ?? byLayer.set(drawnOn, []).get(drawnOn)!).push({ e, m, b });
      } else {
        orphans.push({ e, m, b });
      }
    };
    for (const e of doc.entities) collect(e, IDENTITY);
    return { doc, byLayer, orphans, frame: needFrame ? { pads, lines } : null };
  }

  private drawByLayer(
    ctx: CanvasRenderingContext2D, doc: CadDocument, opts: RenderOptions,
  ): void {
    const n = doc.layers.length;

    // Reuse the flattened tree for a pure viewport move; otherwise rebuild it
    // and refresh the cache, so the next pan/zoom has current geometry.
    let flat: Flatten;
    if (opts.reuseFlatten && this.flatCache && this.flatCache.doc === doc) {
      flat = this.flatCache;
    } else {
      flat = this.buildFlatten(doc);
      this.flatCache = flat;
    }
    this.frame = flat.frame;

    // Deepest (highest index) first, shallowest last; the active layer last of
    // all so it reads on top. Cheap, and depends on the active layer, so it is
    // recomputed every frame rather than cached.
    const order: number[] = [];
    for (let li = n - 1; li >= 0; li--) if (li !== opts.activeLayer) order.push(li);
    if (opts.activeLayer !== undefined && opts.activeLayer >= 0 && opts.activeLayer < n) {
      order.push(opts.activeLayer);
    }

    // What is worth drawing at all, this frame, at this zoom.
    const view = this.viewport.bounds();
    const scale = this.viewport.scale;
    const fast = opts.interacting === true;
    // A thing smaller than about a pixel says nothing the eye can use, and
    // text below a few pixels of cap height cannot be read — both cost the
    // same to draw as the legible kind. The thresholds are looser while the
    // view is moving, and everything is drawn again once it settles.
    const minPixels = fast ? 2 : 1;
    const minTextPixels = fast ? 6 : 4;

    const skip = (p: Placed): boolean => {
      const b = p.b;
      // No box means no opinion: draw it. A missing box must never read as
      // "off screen", or geometry disappears and it looks like data loss.
      if (!b) return false;
      if (!overlaps(b, view)) { this.stats.culled++; return true; }
      const px = Math.max(b.x2 - b.x1, b.y2 - b.y1) * scale;
      if (px < minPixels) { this.stats.tiny++; return true; }
      if (p.e.kind === 'text' && p.e.height * scale < minTextPixels) {
        this.stats.illegible++;
        return true;
      }
      return false;
    };

    // Anything on a layer outside the stack goes underneath everything.
    for (const p of flat.orphans) {
      if (skip(p)) continue;
      this.stats.drawn++;
      this.drawLeaf(ctx, doc, p.e, p.m, opts);
    }

    for (const li of order) {
      const items = flat.byLayer.get(li);
      if (!items) continue;
      // Fills first, under the traces that share the layer (as the original does).
      for (const p of items) {
        if (p.e.kind !== 'fill' || skip(p)) continue;
        this.stats.drawn++;
        this.drawLeaf(ctx, doc, p.e, p.m, opts);
      }
      // Then the rest in document order, but with runs of lines and arcs merged
      // into one path per style — so a flooded plane is a handful of strokes
      // rather than thousands. The batch is flushed before any pad or text, so
      // the on-screen result matches drawing each in order (bar the anti-aliased
      // seams between overlapping flood lines, which merge into a cleaner solid).
      this.strokeBatch = new Map();
      for (const p of items) {
        if (p.e.kind === 'fill') continue;
        if (skip(p)) continue;
        if (p.e.kind !== 'line' && p.e.kind !== 'arc') this.flushBatch(ctx);
        this.stats.drawn++;
        this.drawLeaf(ctx, doc, p.e, p.m, opts);
      }
      this.flushBatch(ctx);
      this.strokeBatch = null;
    }
  }

  private drawEntity(
    ctx: CanvasRenderingContext2D, doc: CadDocument, e: Entity,
    m: Xform, opts: RenderOptions,
  ): void {
    if (e.kind === 'component') {
      const inner = compose(m, e);
      for (const c of e.children) this.drawEntity(ctx, doc, c, inner, opts);
      return;
    }
    this.drawLeaf(ctx, doc, e, m, opts);
  }

  private drawLeaf(
    ctx: CanvasRenderingContext2D, doc: CadDocument, e: Entity,
    m: Xform, opts: RenderOptions,
  ): void {
    const color = this.colorFor(doc, e, opts);
    if (color === null) return;

    switch (e.kind) {
      case 'line': this.drawLine(ctx, e, m, color); break;
      case 'arc': this.drawArc(ctx, e, m, color); break;
      case 'fill': this.drawFill(ctx, doc, e, m, color); break;
      case 'pad': this.drawPad(ctx, doc, e, m, color); break;
      case 'text': this.drawTextEntity(ctx, doc, e, m, color); break;
      case 'spiral': this.drawSpiral(ctx, e, m, color); break;
    }
  }

  private drawLine(
    ctx: CanvasRenderingContext2D, e: LineEntity, m: Xform, color: string,
  ): void {
    const vp = this.viewport;
    const [x1, y1] = tx(e.x1, e.y1, m);
    const [x2, y2] = tx(e.x2, e.y2, m);

    // Outline / sparse-fill modes trace the edges of a straight segment instead
    // of filling it, so overlapping copper stays readable. A bulged (arc)
    // segment falls through to its centre line, which reads the same way.
    if (this.modes.line === DrawMode.Outline || this.modes.line === DrawMode.SparseFill) {
      if (!e.bulge) { this.outlineSegment(ctx, color, x1, y1, x2, y2, e.width); return; }
    }
    // Centre line: a hairline down the middle, whatever the real width.
    const pen = this.modes.line === DrawMode.DenseFill ? this.penWidth(e.width) : 1;

    this.strokePath(ctx, color, pen, (p) => {
      p.moveTo(vp.toScreenX(x1), vp.toScreenY(y1));

      if (e.bulge) {
        // A bulge turns the segment into a circular arc. The bulge angle is the
        // included angle of the arc, so the sagitta follows from half of it.
        const half = (e.bulge * Math.PI) / 360;
        const t = Math.tan(half);
        if (Math.abs(t) > 1e-9) {
          const mx = (x1 + x2) / 2;
          const my = (y1 + y2) / 2;
          const dx = x2 - x1;
          const dy = y2 - y1;
          const cxc = mx - (dy / 2) / t;
          const cyc = my + (dx / 2) / t;
          const r = Math.hypot(x1 - cxc, y1 - cyc);
          let a1 = Math.atan2(y1 - cyc, x1 - cxc);
          let a2 = Math.atan2(y2 - cyc, x2 - cxc);
          // Canvas y is flipped, so the sweep direction inverts. A mirrored
          // view reflects about the vertical axis too: θ → π-θ, sweep reversed.
          let ccw = e.bulge > 0;
          if (vp.mirrored) { a1 = Math.PI - a1; a2 = Math.PI - a2; ccw = !ccw; }
          p.arc(vp.toScreenX(cxc), vp.toScreenY(cyc), r * vp.scale, -a1, -a2, ccw);
          return;
        }
      }

      p.lineTo(vp.toScreenX(x2), vp.toScreenY(y2));
    });
  }

  /**
   * The outline of a round-capped segment — a stadium — stroked as a hairline.
   * This is CIRCAD's outline draw mode (grlinex case 1): the copper's true
   * extent is still shown, but you can see what runs underneath it.
   */
  private outlineSegment(
    ctx: CanvasRenderingContext2D, color: string,
    x1: number, y1: number, x2: number, y2: number, width: number,
  ): void {
    const vp = this.viewport;
    const sx1 = vp.toScreenX(x1);
    const sy1 = vp.toScreenY(y1);
    const sx2 = vp.toScreenX(x2);
    const sy2 = vp.toScreenY(y2);
    const r = Math.max(0.5, (width * vp.scale) / 2);

    this.strokePath(ctx, color, 1, (p) => {
      const dx = sx2 - sx1;
      const dy = sy2 - sy1;
      const len = Math.hypot(dx, dy);
      if (len < 1e-9) { // a zero-length segment is just its end cap
        p.moveTo(sx1 + r, sy1);
        p.arc(sx1, sy1, r, 0, Math.PI * 2);
        return;
      }
      const nx = (-dy / len) * r;
      const ny = (dx / len) * r;
      const an = Math.atan2(dx, -dy); // screen angle of the +normal offset
      p.moveTo(sx1 + nx, sy1 + ny);
      p.lineTo(sx2 + nx, sy2 + ny);
      p.arc(sx2, sy2, r, an, an - Math.PI, true);    // cap at the far end
      p.lineTo(sx1 - nx, sy1 - ny);
      p.arc(sx1, sy1, r, an + Math.PI, an, true);    // cap at the near end
      p.closePath();
    });
  }

  /**
   * A spiral: the radius runs from `r1` to `r2` while the angle runs from `a1`
   * to `a2`, so it is sampled as a polyline rather than being any one arc.
   * Used for coils and spiral inductors — rare, but a file carrying one used to
   * show nothing at all where it should be.
   */
  private drawSpiral(
    ctx: CanvasRenderingContext2D, e: SpiralEntity, m: Xform, color: string,
  ): void {
    const vp = this.viewport;
    const [cx, cy] = tx(e.cx, e.cy, m);
    const scx = vp.toScreenX(cx);
    const scy = vp.toScreenY(cy);
    const baseRot = (Math.atan2(m.sin, m.cos) * 180) / Math.PI;

    let a1 = (e.a1 + baseRot * m.mirror) * m.mirror;
    let a2 = (e.a2 + baseRot * m.mirror) * m.mirror;
    if (vp.mirrored) { a1 = 180 - a1; a2 = 180 - a2; }

    const sweep = a2 - a1;
    // One sample every few degrees keeps the curve smooth without drawing more
    // segments than the screen can show.
    const steps = Math.max(8, Math.min(720, Math.ceil(Math.abs(sweep) / 4)));

    this.strokePath(ctx, color, this.penWidth(e.width), (p) => {
      for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        const ang = (-(a1 + sweep * t) * Math.PI) / 180;
        const r = (e.r1 + (e.r2 - e.r1) * t) * vp.scale;
        const px = scx + r * Math.cos(ang);
        const py = scy + r * Math.sin(ang);
        if (i === 0) p.moveTo(px, py);
        else p.lineTo(px, py);
      }
    });
  }

  private drawArc(
    ctx: CanvasRenderingContext2D, e: ArcEntity, m: Xform, color: string,
  ): void {
    const vp = this.viewport;
    const g = arcGeometry(e, m);
    const r = e.radius * vp.scale;
    if (r <= 0) return;

    let start = g.start;
    let sweep = g.sweep;
    let axis = g.axis;
    const cx = g.cx;
    const cy = g.cy;

    // Mirror View reflects the sheet about its vertical axis, which maps an
    // angle θ to 180-θ and reverses the sweep. Folding it in here lets the
    // screen-angle maths below stay exactly as it is for the normal view.
    if (vp.mirrored) {
      start = 180 - start;
      sweep = -sweep;
      axis = 180 - axis;
    }

    const scx = vp.toScreenX(cx);
    const scy = vp.toScreenY(cy);
    const a0 = -(start * Math.PI) / 180;
    const a1 = -((start + sweep) * Math.PI) / 180;

    // Outline mode draws the two edges of the band the arc sweeps; centre line
    // draws it hairline. Dense fill strokes it at true width, as normal.
    const mode = this.modes.line;
    if (mode === DrawMode.Outline || mode === DrawMode.SparseFill) {
      const half = (e.width * vp.scale) / 2;
      for (const rr of [r - half, r + half]) {
        if (rr <= 0.2) continue;
        this.strokePath(ctx, color, 1, (p) => {
          p.moveTo(scx + rr * Math.cos(a0), scy + rr * Math.sin(a0));
          p.arc(scx, scy, rr, a0, a1, sweep > 0);
        });
      }
      return;
    }

    this.strokePath(ctx, color, mode === DrawMode.DenseFill ? this.penWidth(e.width) : 1, (p) => {
      if (e.eccentricity && e.eccentricity !== 1) {
        const ry = r * e.eccentricity;
        const rot = -(axis * Math.PI) / 180;
        const cr = Math.cos(rot);
        const sr = Math.sin(rot);
        // Start point of the rotated ellipse, so a batched path does not draw a
        // connecting line from the previous segment.
        p.moveTo(
          scx + r * Math.cos(a0) * cr - ry * Math.sin(a0) * sr,
          scy + r * Math.cos(a0) * sr + ry * Math.sin(a0) * cr,
        );
        p.ellipse(scx, scy, r, ry, rot, a0, a1, sweep > 0);
      } else {
        p.moveTo(scx + r * Math.cos(a0), scy + r * Math.sin(a0));
        p.arc(scx, scy, r, a0, a1, sweep > 0);
      }
    });
  }

  private drawFill(
    ctx: CanvasRenderingContext2D, doc: CadDocument, e: FillEntity, m: Xform, color: string,
  ): void {
    if (e.vertices.length < 2) return;
    const vp = this.viewport;

    // A pour carries far more outline than a screen can show: one on this
    // board has ten thousand vertices, and at any ordinary zoom most of them
    // land on a pixel a neighbour already occupies. Those are dropped before
    // being transformed at all — the placement is a rigid motion with a
    // uniform scale, so a distance in world units maps straight to pixels and
    // the test costs a subtraction rather than a matrix.
    //
    // The first vertex is always kept, and so is the last: dropping either
    // would move where the outline starts or closes, which is visible even
    // when the vertex between two others is not.
    // Only an outline with plenty of vertices is thinned. A small shape is
    // cheap to draw whatever its vertex count, and it is precisely where
    // dropping a point does damage: a thermal relief a few pixels across loses
    // a fifth of its area to the same rule that costs a ten-thousand-vertex
    // pour nothing anyone can see. Measured, unguarded thinning cost 18.9% of
    // the worst shape's area; guarded, 1.8% of a shape large enough that it
    // amounts to a sub-pixel shift of one edge.
    const thin = e.vertices.length >= 128;
    const minStep = (this.interacting ? 1.5 : 0.75) / vp.scale;
    const minStep2 = thin ? minStep * minStep : 0;
    const last = e.vertices.length - 1;
    let keptX = 0;
    let keptY = 0;
    let started = false;

    const outline = new Path2D();
    for (let i = 0; i <= last; i++) {
      const v = e.vertices[i]!;
      if (started && i !== last) {
        const dx = v.x - keptX;
        const dy = v.y - keptY;
        if (dx * dx + dy * dy < minStep2) { this.stats.verticesSkipped++; continue; }
      }
      const [px, py] = tx(v.x, v.y, m);
      const sx = vp.toScreenX(px);
      const sy = vp.toScreenY(py);
      if (!started) { outline.moveTo(sx, sy); started = true; } else outline.lineTo(sx, sy);
      keptX = v.x;
      keptY = v.y;
      this.stats.vertices++;
    }
    outline.closePath();

    // An outline, not a solid: the original tells a pour from a hatched
    // boundary by whether a width is set.
    if (e.width > 0) {
      ctx.strokeStyle = color;
      ctx.lineWidth = this.penWidth(e.width);
      ctx.stroke(outline);
      return;
    }

    if (!e.flood) {
      ctx.fillStyle = color;
      ctx.fill(outline);
      return;
    }

    // A pour is built on a surface of its own and then laid over the drawing.
    //
    // Its clearances used to be painted in the background colour straight onto
    // the canvas, which does look like bare board — and hides everything the
    // layers underneath had already drawn. A pour on the top copper blanked
    // the bottom copper wherever it cleared, so half the board went missing
    // behind a plane. Built separately, the clearances are cut out of the pour
    // rather than painted over the drawing, and what is under them shows.
    const scratch = this.scratchFor(ctx);
    if (!scratch) {
      // No second surface to be had: fall back to the old behaviour rather
      // than drawing a pour with no clearances at all, which would be a short.
      ctx.fillStyle = color;
      ctx.fill(outline);
      this.clearFlood(ctx, doc, e, color, hex(this.bg));
      return;
    }

    // Only the part of the canvas the pour can reach is cleared and copied
    // back. A pour covering a corner of the board should not cost a
    // full-window blit, and on a board with several of them it would be one
    // per pour, every frame.
    const sx1 = Math.max(0, Math.floor(vp.toScreenX(e.bounds.x1)) - 2);
    const sx2 = Math.min(ctx.canvas.width, Math.ceil(vp.toScreenX(e.bounds.x2)) + 2);
    // Screen y runs the other way from the drawing's, so the box flips.
    const sy1 = Math.max(0, Math.floor(vp.toScreenY(e.bounds.y2)) - 2);
    const sy2 = Math.min(ctx.canvas.height, Math.ceil(vp.toScreenY(e.bounds.y1)) + 2);
    const sw = sx2 - sx1;
    const sh = sy2 - sy1;
    if (sw <= 0 || sh <= 0) return;          // this pour is not on screen

    scratch.clearRect(sx1, sy1, sw, sh);
    scratch.fillStyle = color;
    scratch.fill(outline);
    this.clearFlood(scratch, doc, e, color, null);
    ctx.drawImage(scratch.canvas, sx1, sy1, sw, sh, sx1, sy1, sw, sh);
  }

  /**
   * A second surface the size of the canvas, for building a pour on.
   *
   * Kept and reused rather than made per pour: allocating a canvas the size of
   * the window several times a frame is the sort of thing that turns a smooth
   * pan into a stuttering one. Null if the browser will not give us one, and
   * the caller has a fallback for that.
   */
  private scratchFor(ctx: CanvasRenderingContext2D): CanvasRenderingContext2D | null {
    const w = ctx.canvas.width;
    const h = ctx.canvas.height;
    if (!this.scratch || this.scratch.canvas.width !== w || this.scratch.canvas.height !== h) {
      const c = document.createElement('canvas');
      c.width = w;
      c.height = h;
      this.scratch = c.getContext('2d');
    }
    return this.scratch;
  }

  private scratch: CanvasRenderingContext2D | null = null;

  /**
   * Clear a poured plane around everything not on its net: a ring around every
   * pad that reaches this layer (through-hole, or a pad on the layer itself)
   * and a gap along every off-net trace sharing the layer.
   *
   * `bg` decides how. Given a colour, the clearances are painted in it, which
   * is the old way and is only used when there is no second surface to build
   * on. Given null, they are cut out — the pour ends up with real holes, and
   * whatever the layers underneath drew shows through them instead of being
   * buried under a plane.
   */
  private clearFlood(
    ctx: CanvasRenderingContext2D, doc: CadDocument, e: FillEntity, color: string,
    bg: string | null,
  ): void {
    const frame = this.frame;
    if (!frame || !e.flood) return;
    const vp = this.viewport;
    const onNet = (sig?: string): boolean => e.signal !== undefined && sig === e.signal;

    // This is the most expensive thing the renderer does, by a wide margin: a
    // knockout for every pad and every track, repeated for every pour. A board
    // with seventy-nine pours, a thousand pads and seven thousand tracks asks
    // for the better part of a million canvas calls to draw one frame.
    //
    // Almost none of them land anywhere useful. A knockout only matters where
    // the pour actually is and where the screen actually looks, so the work is
    // clipped to the overlap of the two before anything is drawn.
    const view = vp.bounds();
    const pour = e.bounds;
    const x1 = Math.max(view.x1, pour.x1);
    const y1 = Math.max(view.y1, pour.y1);
    const x2 = Math.min(view.x2, pour.x2);
    const y2 = Math.min(view.y2, pour.y2);
    if (x1 > x2 || y1 > y2) return;      // this pour is not on screen at all

    // A clearance thinner than a pixel changes nothing anyone can see.
    const minKnockout = 1 / vp.scale;

    ctx.save();
    if (bg === null) {
      // Cut the clearances out, so what is beneath the pour shows through.
      ctx.globalCompositeOperation = 'destination-out';
      ctx.fillStyle = '#000';
      ctx.strokeStyle = '#000';
    } else {
      ctx.fillStyle = bg;
      ctx.strokeStyle = bg;
    }

    // A pad on the pour's own net is either flooded solid or given a thermal
    // relief: cleared like any other, then tied back with four spokes. The
    // spokes are collected here and stroked after every knockout is done, or
    // the next pad's clearance would cut through them.
    const thermal = e.flood.connect === 'thermal';
    // The arms that survived the pour's own connectivity walk. Recomputing
    // them here would put back the ones that reach no copper — the arm in the
    // middle of nowhere, pointing at a pad it must not touch.
    const spokes: Spoke[] = e.flood.spokes ?? [];

    for (const p of frame.pads) {
      if (!(p.through || p.layer === e.layer)) continue; // does not reach this layer
      if (onNet(p.signal) && !thermal) continue;         // flooded solid
      const reach = Math.max(p.rx, p.ry) + e.flood.toPad;
      if (p.x + reach < x1 || p.x - reach > x2
        || p.y + reach < y1 || p.y - reach > y2) { this.stats.knockoutsSkipped++; continue; }
      if (reach * 2 < minKnockout) { this.stats.knockoutsSkipped++; continue; }
      this.stats.knockouts++;

      // The gap around the pad, not the pad as well.
      //
      // Knocking out the whole disc took the pad with it: a through pad is
      // drawn with the pad master, which comes before the copper layer the
      // pour is on, so the pour simply painted over it. What was left was a
      // hole in the plane with nothing in the middle — and once thermal
      // spokes arrived, a star with no hub. Clearing the ring instead leaves
      // the pad standing, which is what a relieved pad looks like: a wheel.
      const outer = padOutline(p.world, e.flood.toPad);
      const inner = padOutline(p.world, 0);
      if (outer.length < 3) continue;
      const ring = new Path2D();
      const trace = (pts: { x: number; y: number }[]): void => {
        pts.forEach((v, i) => {
          const sx = vp.toScreenX(v.x);
          const sy = vp.toScreenY(v.y);
          if (i === 0) ring.moveTo(sx, sy); else ring.lineTo(sx, sy);
        });
        ring.closePath();
      };
      trace(outer);
      if (inner.length >= 3) trace(inner);
      // Even-odd, so the inner loop is the hole rather than more of the fill.
      ctx.fill(ring, 'evenodd');
    }

    ctx.lineCap = 'round';
    for (const ln of frame.lines) {
      if (ln.layer !== e.layer) continue;  // only traces sharing the plane layer
      if (onNet(ln.signal)) continue;
      const pad = ln.width / 2 + e.flood.toLine;
      if (Math.max(ln.x1, ln.x2) + pad < x1 || Math.min(ln.x1, ln.x2) - pad > x2
        || Math.max(ln.y1, ln.y2) + pad < y1 || Math.min(ln.y1, ln.y2) - pad > y2) {
        this.stats.knockoutsSkipped++;
        continue;
      }
      this.stats.knockouts++;
      ctx.lineWidth = (ln.width + e.flood.toLine * 2) * vp.scale;
      ctx.beginPath();
      ctx.moveTo(vp.toScreenX(ln.x1), vp.toScreenY(ln.y1));
      ctx.lineTo(vp.toScreenX(ln.x2), vp.toScreenY(ln.y2));
      ctx.stroke();
    }

    // Copper the clearances cut off from the rest of the pour. Worked out when
    // the pour was made and carried on it, so this is a handful of rectangles
    // rather than a grid walk every frame.
    for (const r of e.flood.islands ?? []) {
      if (r.x2 < x1 || r.x1 > x2 || r.y2 < y1 || r.y1 > y2) continue;
      const sx = vp.toScreenX(r.x1);
      const sy = vp.toScreenY(r.y2);
      ctx.fillRect(sx, sy,
        Math.max(1, (r.x2 - r.x1) * vp.scale),
        Math.max(1, (r.y2 - r.y1) * vp.scale));
    }

    // The spokes go back in last, in the pour's own colour, after every
    // clearance has been taken out — otherwise a neighbouring pad's clearance
    // would cut the star it had just been given.
    if (spokes.length > 0) {
      // Back to laying copper down, after all the cutting away.
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = color;
      ctx.strokeStyle = color;
      ctx.lineCap = 'round';
      for (const s of spokes) {
        ctx.lineWidth = Math.max(1, s.width * vp.scale);
        ctx.beginPath();
        ctx.moveTo(vp.toScreenX(s.x1), vp.toScreenY(s.y1));
        ctx.lineTo(vp.toScreenX(s.x2), vp.toScreenY(s.y2));
        ctx.stroke();
      }
    }
    ctx.restore();
  }

  /**
   * Pads. The eight types split into: schematic-only markers (T0 pin, S3 dot),
   * rectangles (digit 2), and round/oval (digits 0,1,3). Through types get a
   * drill hole knocked out in the background colour.
   */
  private drawPad(
    ctx: CanvasRenderingContext2D, doc: CadDocument, e: PadEntity,
    m: Xform, color: string,
  ): void {
    const vp = this.viewport;
    const [px, py] = tx(e.x, e.y, m);
    const sx = vp.toScreenX(px);
    const sy = vp.toScreenY(py);

    if (padIsSchematic(e.type)) {
      // T0 is a connection point, drawn as a small cross; S3 is a junction dot.
      ctx.strokeStyle = color;
      ctx.fillStyle = color;
      if (e.type === 'S3') {
        ctx.beginPath();
        ctx.arc(sx, sy, Math.max(2, (e.sizeX / 2) * vp.scale), 0, Math.PI * 2);
        ctx.fill();
      } else {
        const k = 3;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(sx - k, sy); ctx.lineTo(sx + k, sy);
        ctx.moveTo(sx, sy - k); ctx.lineTo(sx, sy + k);
        ctx.stroke();
      }
      // A schematic pin is a marker of a fixed few pixels, not a pad with a
      // size, so its number cannot be scaled from the shape the way a pad's is.
      // It follows the zoom instead, and sits above and right of the pin rather
      // than across it — there is no pad here to write on, only the wire.
      if (this.showPins && e.pin) {
        const size = Math.min(14, Math.max(6, vp.scale * 0.055));
        this.queuePinNumber(e.pin, sx + size * 0.7, sy - size * 0.7, size, size * 4, color);
      }
      return;
    }

    const rot = ((e.rotation ?? 0) * Math.PI) / 180 + Math.atan2(m.sin, m.cos) * m.mirror;
    const w = e.sizeX * vp.scale;
    const h = e.sizeY * vp.scale;

    // The pad's shape comes from `padOutline`, the same function the plotter
    // and the realistic views read.
    //
    // This used to work it out here instead, from the CIRCAD type and the two
    // sizes, which can say three things: rectangle, circle, stadium. That is
    // all the original could store. It is not all SirCAD can store — a rounded
    // rectangle, a chamfered pad, a trapezoid, a KiCad custom outline — and
    // every one of those was being drawn as a plain rectangle on screen while
    // the Gerber and the 3D view showed it correctly. The drawing is the one
    // place you look before committing, so it is the last place that should be
    // showing you a different board.
    ctx.save();
    ctx.fillStyle = color;
    ctx.strokeStyle = color;
    ctx.beginPath();

    const shape = padShape(e);
    if (shape.kind === 'round' && Math.abs(w - h) < 0.5) {
      // Kept as a real arc rather than a polygon. Vias outnumber everything
      // else on a board and this is the path they all take.
      ctx.arc(sx, sy, Math.max(0.5, w / 2), 0, Math.PI * 2);
    } else {
      const pts = padOutline(e);
      if (pts.length < 3) { ctx.restore(); return; }
      for (let i = 0; i < pts.length; i++) {
        const v = pts[i]!;
        const [wx, wy] = tx(v.x, v.y, m);
        const px2 = vp.toScreenX(wx);
        const py2 = vp.toScreenY(wy);
        if (i === 0) ctx.moveTo(px2, py2);
        else ctx.lineTo(px2, py2);
      }
      ctx.closePath();
    }

    // Outline / centre-line modes draw the pad hollow, so a pad does not hide
    // the track running under it.
    // Filled unless the mode asks for an outline.
    if (this.modes.pad >= PadDrawMode.Filled) {
      ctx.fill();
    } else {
      ctx.lineWidth = 1;
      ctx.stroke();
    }
    ctx.restore();

    // The hole is knocked out in the pad's own frame, which is where the slot
    // records its far end.
    ctx.save();
    ctx.translate(sx, sy);
    // A mirrored view reflects the sheet, which reverses the sense of rotation.
    ctx.rotate(vp.mirrored ? rot : -rot);

    // A hollow pad already shows the board through it, so the drill knock-out
    // is only needed when the pad is filled.
    // The drill shows through in every mode but the two that suppress it.
    if (e.hole && padThrough(e)
      && this.modes.pad !== PadDrawMode.Outline
      && this.modes.pad !== PadDrawMode.FilledNoHole) {
      if (e.slot) {
        // A routed slot, not a drilled hole: a path of the hole's width from
        // one end to the other, with the pad at its midpoint. Drawn with the
        // pad's own rotation undone, because the slot's far end is recorded in
        // the drawing's frame and would otherwise be turned a second time.
        const undo = vp.mirrored ? -rot : rot;
        ctx.save();
        ctx.rotate(undo);
        // Screen Y runs the other way from the drawing's, so the offset flips.
        const ex = (e.slot.x - e.x) * vp.scale * m.mirror;
        const ey = -(e.slot.y - e.y) * vp.scale;
        ctx.strokeStyle = hex(this.bg);
        ctx.lineWidth = e.hole * vp.scale;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(-ex, -ey);
        ctx.lineTo(ex, ey);
        ctx.stroke();
        ctx.restore();
      } else {
        ctx.fillStyle = hex(this.bg);
        ctx.beginPath();
        ctx.arc(0, 0, (e.hole * vp.scale) / 2, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    ctx.restore();

    if (this.showPins && e.pin) this.queuePinNumber(e.pin, sx, sy, Math.min(w, h) * 0.62, Math.max(w, h) * 0.9, color);
  }

  /**
   * Hold a pin number for the overlay pass.
   *
   * Sized to the pad, so a big pad gets a big number and a via a small one, and
   * dropped once it would be a smudge: below about six pixels a digit is no
   * longer a digit, and drawing it only fogs the copper.
   */
  private queuePinNumber(
    pin: string, sx: number, sy: number, size: number, room: number, color: string,
  ): void {
    if (size < 6) { this.stats.illegible++; return; }
    this.pinLabels.push({ pin, sx, sy, size, room, color });
  }

  /**
   * The pin numbers gathered this frame, written over the finished board.
   *
   * Held upright whatever the pad's rotation: a number is for reading, and a
   * board full of sideways and upside-down ones is worse than none.
   *
   * Each is drawn in its pad's colour over a halo of the background colour. The
   * obvious thing is to knock the number out in the background colour, the way
   * the drill hole is knocked out — but a through pad *is* mostly drill hole,
   * and a background-coloured number sitting on it disappears. The halo works
   * either way round: over copper it clears a gap for the glyph, and over the
   * hole the glyph stands against the board.
   */
  private drawPinNumbers(ctx: CanvasRenderingContext2D): void {
    if (!this.pinLabels.length) return;
    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.lineJoin = 'round';
    for (const l of this.pinLabels) {
      ctx.font = `${Math.round(l.size)}px ui-monospace, monospace`;
      ctx.lineWidth = Math.max(2, l.size / 4);
      ctx.strokeStyle = hex(this.bg);
      // A long pin name would spill past the pad it belongs to, so it is
      // squeezed to fit rather than allowed to run over its neighbours.
      ctx.strokeText(l.pin, l.sx, l.sy, l.room);
      ctx.fillStyle = l.color;
      ctx.fillText(l.pin, l.sx, l.sy, l.room);
      this.stats.drawn++;
    }
    ctx.restore();
  }

  private drawTextEntity(
    ctx: CanvasRenderingContext2D, doc: CadDocument, e: TextEntity, m: Xform,
    color: string,
  ): void {
    const vp = this.viewport;
    // Below a couple of pixels a glyph is an illegible smudge; the original
    // suppresses text at low zoom too.
    if (e.height * vp.scale < 4) return;

    const [px, py] = tx(e.x, e.y, m);
    const baseRot = (Math.atan2(m.sin, m.cos) * 180) / Math.PI;

    // Flipping a symbol on a schematic must not flip its lettering.
    //
    // A mirrored part on a board is genuinely seen from behind, so its silk
    // reads backwards and should be drawn that way. A schematic is a diagram,
    // not a view of an object: turning a gate round to face the other way is a
    // statement about the wiring, and its reference and value have to stay
    // readable. So the glyphs keep their own orientation while the position
    // still moves with the mirror — the label goes where the flip put it, the
    // right way round.
    const keepReadable = doc.kind === 'sch' && m.mirror < 0;

    // The string's own mirror, which is not its part's.
    //
    // A board drawn elsewhere marks each underside string itself, and the
    // render and the film have honoured that since the flag existed. This,
    // the drawing everything else is done on, never read it — so every bottom
    // legend came out forwards and running the wrong way from its insertion.
    // On the ESP32 the line naming the board ran a whole inch off its edge.
    const own = e.mirrored === true;

    const layout = layoutText(
      substituteParams(e.text, m.params ?? {}, m.net ?? undefined),
      {
        x: px,
        y: py,
        height: e.height,
        aspect: e.aspect,
        rotation: keepReadable
          ? (e.rotation ?? 0) + baseRot
          : (e.rotation ?? 0) * m.mirror + baseRot,
        // The point the text springs from moves with the mirror, so the side
        // it grows towards has to move with it as well. A label anchored at
        // the left of a symbol ends up at the right when the part is flipped,
        // and left-anchored text there runs back through the body it was
        // labelling. Flipping the anchor sends it outwards again.
        anchor: keepReadable ? flipAnchorH(e.anchor) : e.anchor,
        // Two mirrors cancel: a marked string inside a flipped part reads
        // forwards again, which is how it was written.
        mirrored: keepReadable ? own : own !== (m.mirror < 0),
      },
    );

    ctx.strokeStyle = color;
    // Stroke weight scales with the glyph, as a pen plotter would — unless the
    // text draw mode asks for the thin, fast rendering.
    ctx.lineWidth = this.modes.text === DrawMode.DenseFill
      ? Math.max(1, e.height * vp.scale * 0.08)
      : 1;
    drawText(ctx, layout, (x) => vp.toScreenX(x), (y) => vp.toScreenY(y));
  }

  // -------------------------------------------------------------------------


  /**
   * Lay the photographs down before anything else is drawn on them.
   *
   * Under the grid as well as under the geometry: grid dots peppered across a
   * photograph make it harder to read, and the grid is there to place things
   * against, which the picture is not.
   *
   * A picture is drawn at its own resolution — inches per pixel — so it sits at
   * the size the board really is, and turns about its insertion point.
   */
  private drawImages(ctx: CanvasRenderingContext2D, opts: RenderOptions): void {
    if (!opts.images?.length) return;
    const vp = this.viewport;

    ctx.save();
    ctx.globalAlpha = Math.max(0, Math.min(1, opts.imageOpacity ?? 1));
    // A photograph enlarged past its own pixels should look like a photograph,
    // not like a mosaic; the browser's own smoothing is what does that.
    ctx.imageSmoothingEnabled = true;

    for (const { ref, pixels } of opts.images) {
      const w = ref.width * ref.resolution;
      const h = ref.height * (ref.resolutionY ?? ref.resolution);
      if (!(w > 0) || !(h > 0)) continue;

      const sx = vp.toScreenX(ref.x);
      const sy = vp.toScreenY(ref.y);
      const sw = w * vp.scale;
      const sh = h * vp.scale;

      ctx.save();
      ctx.translate(sx, sy);
      // Screen y runs the other way from the drawing's, so a turn does too.
      if (ref.rotation) ctx.rotate((-ref.rotation * Math.PI) / 180);
      // The insertion point is the picture's lower-left corner in drawing
      // terms, which on screen — y increasing downward — is its bottom edge.
      ctx.drawImage(pixels, 0, -sh, sw, sh);
      ctx.restore();
    }
    ctx.restore();
  }
  private drawGrid(
    ctx: CanvasRenderingContext2D, doc: CadDocument, opts: RenderOptions,
  ): void {
    const vp = this.viewport;
    let spacing = opts.gridSpacing ?? doc.display?.dotSpacing ?? doc.grid.current;
    if (spacing <= 0) return;

    // Coarsen until dots are at least 8px apart, so the grid never becomes a
    // solid wash at low zoom.
    while (spacing * vp.scale < 8) spacing *= 2;
    if (spacing * vp.scale < 8) return;

    const b = vp.bounds();
    const x0 = Math.ceil(b.x1 / spacing) * spacing;
    const y0 = Math.ceil(b.y1 / spacing) * spacing;

    ctx.fillStyle = hex(doc.display?.dotColor ?? 0x556070);
    const size = vp.scale * spacing > 40 ? 2 : 1;
    for (let x = x0; x <= b.x2; x += spacing) {
      const sx = Math.round(vp.toScreenX(x));
      for (let y = y0; y <= b.y2; y += spacing) {
        ctx.fillRect(sx, Math.round(vp.toScreenY(y)), size, size);
      }
    }
  }

  /**
   * The congestion map, and the parts that are in the wrong place.
   *
   * A ramp below capacity and a hard outline above it. Over capacity is a
   * boolean fact — those tracks do not fit — and a colour ramp alone hides it
   * among the cells that are merely busy.
   */
  private drawDensity(ctx: CanvasRenderingContext2D, d: DensityOverlay): void {
    const vp = this.viewport;
    ctx.save();

    const w = Math.max(1, Math.ceil(vp.scale * d.cell));
    for (let j = 0; j < d.ny; j++) {
      for (let i = 0; i < d.nx; i++) {
        const p = d.pressure[j * d.nx + i]!;
        if (!(p > 0.25)) continue;
        const sx = vp.toScreenX(d.x0 + i * d.cell);
        const sy = vp.toScreenY(d.y0 + (j + 1) * d.cell);
        if (sx + w < 0 || sy + w < 0 || sx > ctx.canvas.width || sy > ctx.canvas.height) continue;

        if (!Number.isFinite(p)) {
          // Traffic through a cell nothing can pass, which is not a degree of
          // busy but a different thing entirely.
          ctx.fillStyle = 'rgba(120,0,140,0.55)';
          ctx.fillRect(sx, sy, w, w);
          continue;
        }
        ctx.fillStyle = rampColour(p);
        ctx.fillRect(sx, sy, w, w);
        if (p > 1) {
          ctx.strokeStyle = 'rgba(255,60,40,0.9)';
          ctx.lineWidth = 1;
          ctx.strokeRect(sx + 0.5, sy + 0.5, w - 1, w - 1);
        }
      }
    }

    for (const f of d.faults) {
      if (f.pts.length < 2) continue;
      ctx.strokeStyle = f.kind === 'overlap' ? 'rgba(255,70,70,0.95)'
        : f.kind === 'straddle' ? 'rgba(255,160,40,0.95)'
          : 'rgba(120,180,255,0.8)';
      ctx.lineWidth = 2;
      ctx.setLineDash(f.kind === 'parked' ? [4, 3] : []);
      ctx.beginPath();
      ctx.moveTo(vp.toScreenX(f.pts[0]!.x), vp.toScreenY(f.pts[0]!.y));
      for (let k = 1; k < f.pts.length; k++) {
        ctx.lineTo(vp.toScreenX(f.pts[k]!.x), vp.toScreenY(f.pts[k]!.y));
      }
      ctx.closePath();
      ctx.stroke();
    }

    ctx.restore();
  }

  private drawRatsnest(
    ctx: CanvasRenderingContext2D, doc: CadDocument, rats: RatLine[],
  ): void {
    const vp = this.viewport;
    // The stock stack has a dedicated Ratnest layer, and hiding it should hide
    // the ratsnest — that is what the layer is for.
    const layer = doc.layers.find((l) => /ratnest|ratsnest/i.test(l.name));
    if (layer && !layer.visible) return;

    const colour = hex(layer?.screen ?? doc.display?.attentionColor ?? 0xffff00);

    ctx.save();
    // A hairline dashed line is close to invisible against the mid-grey
    // ground the stock palette assumes, which made a working ratsnest look
    // like a broken one. Draw a dark casing under a bright dash so it reads
    // over copper, silk and background alike.
    ctx.lineCap = 'butt';
    ctx.setLineDash([]);
    ctx.strokeStyle = 'rgba(0,0,0,0.45)';
    ctx.lineWidth = 3;
    ctx.beginPath();
    for (const r of rats) {
      ctx.moveTo(vp.toScreenX(r.x1), vp.toScreenY(r.y1));
      ctx.lineTo(vp.toScreenX(r.x2), vp.toScreenY(r.y2));
    }
    ctx.stroke();

    ctx.strokeStyle = colour;
    ctx.lineWidth = 1.5;
    ctx.setLineDash([5, 3]);
    ctx.beginPath();
    for (const r of rats) {
      ctx.moveTo(vp.toScreenX(r.x1), vp.toScreenY(r.y1));
      ctx.lineTo(vp.toScreenX(r.x2), vp.toScreenY(r.y2));
    }
    ctx.stroke();

    // Mark the endpoints, so a short ratline between adjacent pads is still
    // findable when the dashes fall between them.
    ctx.setLineDash([]);
    ctx.fillStyle = colour;
    for (const r of rats) {
      for (const [x, y] of [[r.x1, r.y1], [r.x2, r.y2]] as const) {
        ctx.beginPath();
        ctx.arc(vp.toScreenX(x), vp.toScreenY(y), 2, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    ctx.restore();
  }
}

/**
 * Pressure to a colour: green through amber to red, saturating at twice
 * capacity. Alpha rises with it, so quiet board stays readable underneath.
 */
function rampColour(p: number): string {
  const t = Math.min(1, p / 2);
  const r = Math.round(60 + 195 * Math.min(1, t * 2));
  const g = Math.round(200 - 170 * Math.max(0, t * 2 - 1) - 40 * t);
  return `rgba(${r},${Math.max(0, g)},60,${(0.18 + 0.42 * t).toFixed(3)})`;
}

/** Blend a colour toward the background by `amount` (0 = unchanged, 1 = gone). */
function dim(rgb: number, bg: number, amount: number): string {
  const mix = (a: number, b: number): number => Math.round(a + (b - a) * amount);
  const r = mix((rgb >> 16) & 255, (bg >> 16) & 255);
  const g = mix((rgb >> 8) & 255, (bg >> 8) & 255);
  const b = mix(rgb & 255, bg & 255);
  return `rgb(${r},${g},${b})`;
}

/** Layer type badge text, used by the layer panel. */
export function layerTypeLabel(l: Layer): string {
  const names: Record<number, string> = {
    [LayerType.Silk]: 'Silk',
    [LayerType.Mask]: 'Mask',
    [LayerType.Copper]: 'Copper',
    [LayerType.Plane]: 'Plane',
    [LayerType.Symbols]: 'Symbols',
    [LayerType.Paste]: 'Paste',
    [LayerType.Schematic]: 'Signal',
    [LayerType.Drawing]: 'Drawing',
  };
  const base = names[l.type] ?? '?';
  return l.bottom ? `${base} · bottom` : base;
}
